<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue'
import { Combobox, createListCollection } from '@ark-ui/vue'
import UiIcon from './UiIcon.vue'
import UiFieldContainer from './UiFieldContainer.vue'
import UiComboboxGroup from './UiComboboxGroup.vue'
import type { ComboboxGroupData } from './types/combobox'

export interface UiComboboxOption {
    label: string
    value: string
    disabled?: boolean
}

export interface UiComboboxProps {
    name?: string
    id?: string
    options: UiComboboxOption[]
    optionGroups?: ComboboxGroupData[]
    placeholder?: string
    disabled?: boolean
    size?: 'md' | 'lg'
    hint?: string
    hintContent?: string
    error?: string
    invalid?: boolean
    multiple?: boolean
    /** Whether to loop keyboard navigation through items (default: true) */
    loopFocus?: boolean
    /** Whether to open the combobox on arrow key press (default: true) */
    openOnKeyPress?: boolean
    /** Whether to open the combobox on initial click (default: false) */
    openOnClick?: boolean
    /** 
     * Auto-completion behavior:
     * - 'none': No auto-behavior (default)
     * - 'autohighlight': Automatically highlight the first item when dropdown opens or as user types
     * - 'autocomplete': Arrow keys select items and update the input value
     */
    inputBehavior?: 'none' | 'autohighlight' | 'autocomplete'
}

const props = withDefaults(defineProps<UiComboboxProps>(), {
    options: () => [],
    optionGroups: () => [],
    size: 'md',
    multiple: false,
    loopFocus: true,
    openOnKeyPress: true,
    openOnClick: false,
    inputBehavior: 'autohighlight',
})

// Match UiSelect model signature for drop-in compatibility
const model = defineModel<string[] | string>()

const emit = defineEmits<{
    (e: 'validate'): void
}>()

const isOpen = ref(false)
const inputValue = ref('')

const describedByBase = computed(() => props.id || props.name)

const groupedOptions = computed<ComboboxGroupData[]>(() => {
    if (props.optionGroups.length) return props.optionGroups
    return [{
        id: 'default',
        label: '',
        items: props.options.map(opt => ({
            value: opt.value,
            label: opt.label,
            disabled: opt.disabled,
        })),
    }]
})

const filteredGroups = computed<ComboboxGroupData[]>(() => {
    const query = inputValue.value.toLowerCase()
    if (!query) return groupedOptions.value

    return groupedOptions.value
        .map(group => ({
            ...group,
            items: group.items.filter(item =>
                item.label.toLowerCase().includes(query) || item.value.toLowerCase().includes(query),
            ),
        }))
        .filter(group => group.items.length > 0)
})

const filteredOptions = computed(() =>
    filteredGroups.value.flatMap(group => group.items.map(item => ({
        label: item.label,
        value: item.value,
        disabled: item.disabled,
    }))),
)

const collection = computed(() => createListCollection({
    items: filteredOptions.value,
    itemToValue: (item: UiComboboxOption) => item.value,
    itemToString: (item: UiComboboxOption) => item.label,
    isItemDisabled: (item: UiComboboxOption) => !!item.disabled,
}))

// Internal array model for Combobox (Ark UI expects string[] even for single)
const internalValue = ref<string[]>([])

function arraysEqual(a: string[], b: string[]) {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
    return true
}

// Sync external v-model -> internal
watch(model, (newVal) => {
    const incoming = props.multiple
        ? (Array.isArray(newVal) ? newVal : (newVal ? [newVal] : []))
        : (Array.isArray(newVal) ? (newVal[0] ? [newVal[0]] : []) : (newVal ? [newVal] : []))
    if (!arraysEqual(internalValue.value, incoming)) internalValue.value = incoming
}, { immediate: true })

// Sync internal -> external v-model
watch(internalValue, (val, oldVal) => {
    if (props.multiple) {
        if (Array.isArray(model.value) && arraysEqual(model.value as string[], val)) return
        model.value = val as any
    } else {
        const first = val[0]
        if (!Array.isArray(model.value) && model.value === first) return
        model.value = first as any
        // Close dropdown when a value is selected in single-select mode
        if (first && !arraysEqual(val, oldVal)) {
            isOpen.value = false
        }
    }
    emit('validate')
})

// Clear search input when dropdown closes, and force repositioning when opening
watch(isOpen, (newIsOpen) => {
    inputValue.value = ''
    
    // Force repositioning when dropdown opens to fix SSR positioning issues
    // Use requestAnimationFrame after nextTick to ensure lazy-mounted content is rendered
    if (newIsOpen && typeof window !== 'undefined') {
        nextTick(() => {
            requestAnimationFrame(() => {
                window.dispatchEvent(new Event('resize'))
            })
        })
    }
})

const valueToLabel = computed<Record<string, string>>(() => {
    const map: Record<string, string> = {}
    for (const opt of props.options) map[opt.value] = opt.label
    return map
})

const triggerLabel = computed(() => {
    if (props.multiple) {
        const values = internalValue.value
        if (!values.length) return ''
        return values.map(v => valueToLabel.value[v] ?? v).join(', ')
    } else {
        const value = internalValue.value[0]
        return value ? (valueToLabel.value[value] ?? value) : ''
    }
})

function getGroupItems(groupId: string) {
    return filteredGroups.value.find(group => group.id === groupId)?.items || []
}
</script>

<template>
    <UiFieldContainer :size="props.size" :invalid="props.invalid" :hint="props.hint" :hintContent="props.hintContent" :error="props.error" :disabled="props.disabled"
        :describedByIdBase="describedByBase" :isFocused="isOpen">
        <Combobox.Root class="w-full" :collection="collection" v-model="internalValue" :multiple="props.multiple" :disabled="props.disabled"
            :id="props.id || props.name" v-model:open="isOpen" v-model:inputValue="inputValue"
            :loopFocus="props.loopFocus" :openOnKeyPress="props.openOnKeyPress" :openOnClick="props.openOnClick"
            :inputBehavior="props.inputBehavior" lazyMount>
            <Combobox.Control>
                <Combobox.Trigger class="w-full flex items-center justify-between" :class="props.disabled ? 'cursor-not-allowed text-se-gray-500' : 'cursor-pointer'">
                    <span class="text-left truncate w-full">
                        {{ triggerLabel || props.placeholder || 'Select...' }}
                    </span>
                    <UiIcon name="chevron-down" class="w-4 h-4" :class="props.disabled ? 'text-se-gray-400' : 'text-se-gray-700'" />
                </Combobox.Trigger>
            </Combobox.Control>
            <Teleport to="body" defer>
                <Combobox.Positioner class="!z-[100]">
                    <Combobox.Content
                        class="border-1 border-se-gray-300 rounded-md shadow-md bg-se-white my-2 overflow-x-hidden max-h-[min(calc(var(--available-height)-var(--spacing)*4-var(--safe-area-top,0px)-var(--safe-area-bottom,0px)-32px),400px)] z-100"
                        :class="{
                            'w-[calc(var(--reference-width)+var(--spacing)*16)] -ml-8': props.size === 'lg',
                            'w-[calc(var(--reference-width)+var(--spacing)*8)] -ml-4': props.size === 'md',
                        }">

                        <div class="sticky top-0 z-10 flex items-center gap-2 border-b border-se-gray-200 bg-se-white px-3 py-2">
                            <UiIcon name="search" class="w-5 h-5 text-se-gray-500 shrink-0" />
                            <Combobox.Input placeholder="Search…" class="min-w-0 w-full bg-transparent outline-none" autocomplete="off" />
                        </div>

                        <Combobox.Empty>
                            <div class="px-4 py-2 text-se-gray-500">No results found</div>
                        </Combobox.Empty>

                        <template v-for="group in filteredGroups" :key="group.id">
                            <UiComboboxGroup v-if="group.label" :id="group.id" :label="group.label">
                                <Combobox.Item v-for="item in getGroupItems(group.id)" :key="item.value" :item="item"
                                    class="flex items-center justify-between py-2 cursor-pointer data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed hover:bg-se-gray-100 data-[highlighted]:bg-se-gray-100" :class="{
                                        'px-8': props.size === 'lg',
                                        'px-4': props.size === 'md',
                                    }">
                                    <Combobox.ItemText>{{ item.label }}</Combobox.ItemText>
                                    <div class="flex items-center gap-2">
                                        <Combobox.ItemIndicator>
                                            <div class="inline-flex items-center justify-center w-4 h-4 rounded border-1 border-se-primary bg-se-primary">
                                                <UiIcon name="check" class="w-3 h-3 text-se-white" />
                                            </div>
                                        </Combobox.ItemIndicator>
                                    </div>
                                </Combobox.Item>
                            </UiComboboxGroup>

                            <template v-else>
                                <Combobox.Item v-for="item in getGroupItems(group.id)" :key="item.value" :item="item"
                                    class="flex items-center justify-between py-2 cursor-pointer data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed hover:bg-se-gray-100 data-[highlighted]:bg-se-gray-100" :class="{
                                        'px-8': props.size === 'lg',
                                        'px-4': props.size === 'md',
                                    }">
                                    <Combobox.ItemText>{{ item.label }}</Combobox.ItemText>
                                    <div class="flex items-center gap-2">
                                        <Combobox.ItemIndicator>
                                            <div class="inline-flex items-center justify-center w-4 h-4 rounded border-1 border-se-primary bg-se-primary">
                                                <UiIcon name="check" class="w-3 h-3 text-se-white" />
                                            </div>
                                        </Combobox.ItemIndicator>
                                    </div>
                                </Combobox.Item>
                            </template>
                        </template>
                    </Combobox.Content>
                </Combobox.Positioner>
            </Teleport>
        </Combobox.Root>
    </UiFieldContainer>
    
</template>
